"use client";

import React, { useState } from "react";
import { Check, Clock, Info, Sparkles, ArrowRight, ZoomIn } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const servicesData = [{
  id: "exterior",
  title: "Exterior Precision",
  price: "$80 - $120",
  duration: "60 - 90 mins",
  description: "Our signature multi-stage hand wash utilizing deionized water and PH-neutral lubricants to ensure a swirl-free, high-gloss finish.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/exterior_wash_thumbnail.png",
  features: ["Deionized Water Rinse", "Wheel Barrel Deep Clean", "Liquid Paint Sealant", "Precision Air Blow-Dry", "Trim UV Protection", "Glass Clarity Polish"]
}, {
  id: "interior",
  title: "Interior Purge",
  price: "$150 - $180",
  duration: "2.5 - 4 hours",
  description: "A clinical-grade deep clean targeting every fiber and crevice. We extract contaminants and restore factory-fresh textures.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/interior_detailing_thumbnail.png",
  features: ["Steam Vapor Extraction", "Leather pH Balancing", "Anti-Static Dash Coating", "Fiber Protection Treatment", "Ozone Odor Elimination", "Precision Crevice Detailing"]
}, {
  id: "elite",
  title: "Bathers Elite Package",
  price: "$200- $400",
  duration: "5 - 7 hours",
  description: "The complete transformation. We combine our most advanced techniques to rejuvenate your vehicle to showroom standards.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/full_detail_package_thumbnail.png",
  features: ["Full Correction & Sealant", "Level 3 Interior Recovery", "Engine Bay Degreasing", "Headlight Clarity Restore", "Undercarriage Rinse", "Premium Scent Profiling"]
}];
export default function OurDetailingMenu() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="23894" id="select-your-treatment" ref={ref} className="dark bg-[#0d0f14] text-[#e2e8f0] py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-[128px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-sm font-mono text-cyan-400 tracking-widest uppercase mb-3">
            Service Menu
          </h2>
          <h3 className="text-4xl md:text-5xl font-extrabold tracking-tighter mb-6 bg-gradient-to-r from-slate-100 via-slate-300 to-slate-500 bg-clip-text text-transparent uppercase">
            Select Your Treatment
          </h3>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg font-sans">
            Meticulous car care engineered for longevity. Choose the precision package that matches your vehicle's specific requirements.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-8">
          {servicesData.map((service, i) => <div key={service.id} data-index={i} className={`transition-all duration-700 delay-[${i * 100}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              <Card className="h-full border-slate-800 bg-[#1a1d23]/60 backdrop-blur-xl hover:border-cyan-500/50 hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-300 group flex flex-col overflow-hidden rounded-sm">
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <CardTitle className="text-2xl font-extrabold text-slate-100 group-hover:text-cyan-400 transition-colors tracking-tight">
                        {service.title}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-2 text-slate-500 text-sm font-mono">
                        <Clock className="w-4 h-4 text-cyan-400" />
                        <span>{service.duration}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="block text-xl font-extrabold text-slate-100 tracking-tighter">{service.price}</span>
                      <span className="text-xs text-slate-500 font-mono uppercase tracking-widest">est. cost</span>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="flex-grow space-y-6">
                  {}
                  <Dialog>
                    <DialogTrigger asChild>
                      <div className="relative w-full h-48 rounded-sm overflow-hidden cursor-pointer group/image border border-slate-800">
                        <img src={service.image} alt={`${service.title} precision detail`} className="w-full h-full object-cover transition-transform duration-700 group-hover/image:scale-105" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/image:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <div className="flex items-center gap-2 text-white font-mono text-xs uppercase tracking-widest bg-cyan-500/20 px-4 py-2 rounded-sm backdrop-blur-md border border-cyan-500/30">
                            <ZoomIn className="w-4 h-4" />
                            <span>View Macro Detail</span>
                          </div>
                        </div>
                      </div>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-3xl bg-[#0d0f14] border-slate-800 p-0 overflow-hidden rounded-sm">
                      <DialogHeader className="p-6 bg-[#0d0f14]/95 backdrop-blur absolute top-0 left-0 right-0 z-10 border-b border-slate-800">
                        <DialogTitle className="text-slate-100 font-extrabold uppercase tracking-tight">{service.title}</DialogTitle>
                        <DialogDescription className="text-slate-500 font-mono text-xs uppercase">Precision visual documentation.</DialogDescription>
                      </DialogHeader>
                      <div className="w-full h-[60vh] mt-0">
                        <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                      </div>
                    </DialogContent>
                  </Dialog>

                  <p className="text-slate-400 leading-relaxed font-sans text-sm">
                    {service.description}
                  </p>

                  {}
                  <Accordion type="single" collapsible className="w-full border-t border-slate-800">
                    <AccordionItem value="details" className="border-b-0">
                      
                      <AccordionContent>
                        
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>

                <CardFooter className="pt-2">
                  <Button asChild className="w-full h-12 text-sm font-extrabold uppercase tracking-widest bg-cyan-500 hover:bg-cyan-400 text-black shadow-lg shadow-cyan-500/20 rounded-sm transition-all duration-300" size="lg">
                    <Link to="/GetInTouchPage">
                      Reserve Treatment
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>

        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-slate-900/50 border border-slate-800 text-xs font-mono uppercase tracking-widest text-slate-500">
            <Info className="w-4 h-4 text-cyan-400" />
            <span>Final quotes adjusted for vehicle size and surface condition.</span>
          </div>
        </div>
      </div>
    </section>;
}